
import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { sanitizeInternalPath } from '@/utils/url';
import { AuthShellSkeleton } from '@/components/ui/skeleton-block';

type ProtectedRouteProps = {
  children?: React.ReactNode;
  requiredRole?: 'admin' | 'business_owner' | 'staff';
};

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole
}) => {
  const { user, profile, loading } = useAuth();
  const location = useLocation();
  const [timedOut, setTimedOut] = useState(false);

  // Fallback: if auth loading hangs, treat as unauthenticated after a short grace period
  useEffect(() => {
    if (!loading) return;
    const id = setTimeout(() => setTimedOut(true), 2000);
    return () => clearTimeout(id);
  }, [loading]);

  if (loading && !timedOut) {
    return <AuthShellSkeleton />;
  }

  // Not authenticated
  if (!user) {
    const rawNext = `${location.pathname}${location.search}${location.hash}`;
    const safeNext = sanitizeInternalPath(rawNext) || '/dashboard';
    const next = encodeURIComponent(safeNext);
    return <Navigate to={`/fancy-signin?next=${next}`} state={{ from: location }} replace />;
  }

  // Check for required role if specified
  if (requiredRole && profile?.role !== requiredRole) {
    // If user doesn't have the required role, redirect to dashboard
    return <Navigate to="/dashboard" replace />;
  }

  return children ? <>{children}</> : <Outlet />;
};
